import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom'; 
import api from '../axiosConfig';
import errorHandler from '../utils/errorHandler';
import './knowledge_base.css';

const KBArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [tocItems, setTocItems] = useState([]);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  useEffect(() => {
    if (article) {
      generateTableOfContents();
      window.scrollTo(0, 0);
    }
  }, [article]);

  const loadArticle = async () => {
    try {
      const response = await api.get(`/kb/articles/${slug}`); 
      setArticle(response.data.article);
      setRelatedArticles(response.data.related);
      setLoading(false);
    } catch (error) {
      console.error('Error loading article:', error);
      setLoading(false);
    }
  };

  const generateTableOfContents = () => {
    // Extract h2 and h3 headings from content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = article.content;
    
    const headings = tempDiv.querySelectorAll('h2, h3');
    const items = Array.from(headings).map((heading, index) => {
      const id = `heading-${index}`;
      heading.id = id;
      return {
        id,
        text: heading.textContent,
        level: heading.tagName.toLowerCase()
      };
    });
    
    setTocItems(items);
  };

const handleHelpful = async (helpful) => {
    try {
      await api.post(`/kb/articles/${article.id}/helpful`, { 
        helpful
      });
      setFeedbackGiven(true);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="article-loading">
        <div className="spinner"></div>
        <p>Loading article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="article-not-found">
        <h1>Article Not Found</h1>
        <p>Sorry, we couldn't find the article you're looking for.</p>
        <Link to="/kb" className="btn-primary">Back to Knowledge Base</Link>
      </div>
    );
  }

  return (
    <div className="kb-article-page">
      <div className="container">
        <div className="article-layout">
          {/* Sidebar with TOC */}
          {tocItems.length > 0 && (
            <aside className="article-sidebar">
              <div className="toc-container sticky">
                <h4>Table of Contents</h4>
                <nav className="toc">
                  {tocItems.map(item => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className={`toc-item toc-${item.level}`}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToHeading(item.id);
                      }}
                    >
                      {item.text}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          )}

          {/* Main Article Content */}
          <article className="article-content">
            {/* Breadcrumb */}
            <nav className="breadcrumb">
              <Link to="/kb">Knowledge Base</Link>
              <span className="separator">›</span>
              {article.category_name && (
                <>
                  <Link to={`/kb/categories/${article.category_id}`}>
                    {article.category_name}
                  </Link>
                  <span className="separator">›</span>
                </>
              )}
              <span className="current">{article.title}</span>
            </nav>

            {/* Article Header */}
            <header className="article-header">
              <h1>{article.title}</h1>
              
              <div className="article-meta">
                {article.category_name && (
                  <span className="category-badge">{article.category_name}</span>
                )}
                <span className="meta-item">
                  <i className="far fa-eye"></i> {article.views} views
                </span>
                <span className="meta-item">
                  <i className="far fa-clock"></i>
                  Updated {new Date(article.updated_at).toLocaleDateString()}
                </span>
              </div>
            </header>

            {/* Article Body */}
            <div 
              className="article-body"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Feedback Section */}
            <div className="article-feedback">
              <h3>Was this article helpful?</h3>
              {!feedbackGiven ? (
                <div className="feedback-buttons">
                  <button 
                    className="btn-helpful yes"
                    onClick={() => handleHelpful(true)}
                  >
                    <i className="fas fa-thumbs-up"></i> Yes
                  </button>
                  <button 
                    className="btn-helpful no"
                    onClick={() => handleHelpful(false)}
                  >
                    <i className="fas fa-thumbs-down"></i> No
                  </button>
                </div>
              ) : (
                <div className="feedback-thanks">
                  <i className="fas fa-check-circle"></i>
                  <p>Thank you for your feedback!</p>
                </div>
              )}
              
              {article.helpful_yes > 0 && (
                <p className="feedback-stats">
                  {article.helpful_yes} of {article.helpful_yes + article.helpful_no} people 
                  found this helpful
                </p>
              )}
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="related-articles">
                <h3>Related Articles</h3>
                <div className="related-grid">
                  {relatedArticles.map(related => (
                    <Link
                      key={related.id}
                      to={`/kb/articles/${related.slug}`}
                      className="related-card"
                    >
                      <h4>{related.title}</h4>
                      <p>{related.excerpt}</p>
                      <span className="read-more">
                        Read more <i className="fas fa-arrow-right"></i>
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Back to KB */}
            <div className="article-footer">
              <Link to="/kb" className="btn-back">
                <i className="fas fa-arrow-left"></i> Back to Knowledge Base
              </Link>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
};

export default KBArticle;